// Phase 1.ζ-1 — pure unlock-state derivation for the §10.4 locked-UX UI.
//
// Placement rationale: deliberately NOT in src/lib/stripe.js. This helper
// reads a `group` row (DB-schema concept — unlocked_at, lockout_at,
// golf_config) and returns a state tuple the UI uses to pick a §10.4
// cell. It has nothing to do with the Stripe payment client; the 1.ζ-2
// components will import this independently of any Stripe code path.
// Co-locating in stripe.js would couple DB-row interpretation to the
// payment integration and inflate stripe.js further (already 322 lines).
//
// Pure + synchronous: no Supabase calls, no Stripe calls, no globals.
// Single argument (a row from getUserGroups in src/lib/db.js, which after
// 1.ζ-1 returns unlocked_at + lockout_at + golf_config in its select).
//
// The flag-off contract: this function returns the same shape regardless
// of paidPathActive / stripeEnabled. Callers gating UI on those flags
// can still call this safely — it never reads them.

// Returns:
//   {
//     unlocked:        boolean,   // group.unlocked_at != null
//     divisionsStatus: 'awaiting_odds' | 'frozen' | null,
//                                 // null for non-divisions contest_type;
//                                 // 'awaiting_odds' when golf_config.snapshotStatus
//                                 // === 'awaiting_odds'; else 'frozen'.
//     lockoutPassed:   boolean,   // mirrors the unlock_group RPC's lockout
//                                 // guard (migrations/041_pool_unlocks.sql
//                                 // lines 138-148). When true AND !unlocked,
//                                 // the pool is in the §10.6 dead-pool
//                                 // terminal state.
//   }
//
// `unlocked_at` is the SOLE determinant of unlocked vs locked. No global
// flag gates this; the field is server-authoritative (only the
// unlock_group RPC under SUPABASE_SERVICE_KEY can set it).
export function deriveUnlockState(group) {
  if (!group) return { unlocked: false, divisionsStatus: null, lockoutPassed: false }

  const unlocked = group.unlocked_at != null

  // divisionsStatus is meaningful only for the divisions contest_type
  // (Golf:divisions pools). For everything else this dimension is N/A —
  // return null so the UI can branch on it explicitly rather than
  // accidentally rendering a "frozen" badge on a UFC pool.
  let divisionsStatus = null
  if (group.contest_type === 'divisions') {
    divisionsStatus =
      group.golf_config?.snapshotStatus === 'awaiting_odds'
        ? 'awaiting_odds'
        : 'frozen'
  }

  // lockoutPassed mirrors migrations/041_pool_unlocks.sql:138-148 (the
  // unlock_group RPC's lockout guard) and server/index.js's pre-flight
  // copy of the same check (~lines 871-882). Two branches:
  //
  //   (a) lockout_at IS NOT NULL AND lockout_at <= now()   → passed
  //   (b) lockout_at IS NULL AND start_date IS NOT NULL
  //       AND start_date < today's calendar date           → passed
  //
  // Branch (b) uses lexicographic YYYY-MM-DD string compare because
  // groups.start_date is a SQL `date`, not a timestamp — same comparison
  // the server runs at line 877 (`pool.start_date < todayStr`).
  let lockoutPassed = false
  if (group.lockout_at != null) {
    const lockoutMs = Date.parse(group.lockout_at)
    if (Number.isFinite(lockoutMs) && lockoutMs <= Date.now()) {
      lockoutPassed = true
    }
  } else if (group.start_date) {
    const todayStr = new Date().toISOString().slice(0, 10)
    if (group.start_date < todayStr) lockoutPassed = true
  }

  return { unlocked, divisionsStatus, lockoutPassed }
}
